import { useEffect, useRef, useState } from 'react';

/**
 * The ring of bars at the centre of the candidate's interview page.
 *
 * Ported from the "Voice Agent Visualizer" Claude Design. The drawing - bar
 * geometry, colour ramp, outer ticks, core glow, halo - follows that design;
 * what changes is where the numbers come from. The design read a microphone it
 * opened itself. Here the interviewer's audio drives the ring while it speaks,
 * and the candidate's microphone while it listens, both through the ElevenLabs
 * SDK the page already holds.
 *
 * It is on screen for the whole visit, not just the call: before the interview
 * starts and after it ends it idles quietly, so the page never swaps one visual
 * for another. The dark scenery behind it belongs to the page, not to this
 * component - the ring is meant to read as part of the background rather than
 * as something in a frame.
 *
 * The component takes plain getter functions rather than the conversation
 * object, so it has no idea the SDK exists - which keeps a rendering concern
 * free of it and let it be checked against fake audio without a live call.
 */

type Mode = 'idle' | 'speaking' | 'listening';

export interface AgentVoiceStatus {
  title: string;
  detail?: string;
}

export interface AgentVoiceVisualizerProps {
  /** Whether a conversation is running. When false the ring idles and reads no audio. */
  active: boolean;
  /**
   * The label above the ring. Leave undefined during a call for the automatic
   * Speaking / Listening labels; pass null to show no label, where the page's
   * own text already explains the moment.
   */
  status?: AgentVoiceStatus | null;
  /** Byte frequency data (0..255 per bin) for the interviewer's voice. */
  getAgentFrequencies: () => Uint8Array;
  /** Byte frequency data for the candidate's microphone. */
  getCandidateFrequencies: () => Uint8Array;
  /** 0..1 volume of the interviewer's voice. */
  getAgentLevel: () => number;
  /** 0..1 volume of the candidate's microphone. */
  getCandidateLevel: () => number;
  /** The SDK's own verdict on whether the interviewer is talking right now. */
  agentSpeaking: boolean;
}

const BAR_COUNT = 108;
/** The design's accent hue, and how far the ramp travels round the ring. */
const HUE = 288;
const HUE_SPREAD = 42;
/** Below this a volume counts as silence rather than a quiet voice. */
const SILENCE = 0.02;

const LIVE_LABELS: Record<Exclude<Mode, 'idle'>, AgentVoiceStatus> = {
  speaking: { title: 'Speaking', detail: 'The interviewer is talking' },
  listening: { title: 'Listening', detail: 'Go ahead and answer out loud' },
};

/** How lively the ring is in each mode, from the design. */
function envelope(mode: Mode, t: number): number {
  if (mode === 'idle') return 0.16 + 0.06 * Math.sin(t * 0.9);
  if (mode === 'listening') return 0.34 + 0.14 * Math.sin(t * 1.6);
  return 0.72 + 0.24 * Math.sin(t * 5.3) * Math.sin(t * 1.7);
}

/** An SDK getter that throws must not take the interview page down with it. */
function read<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

const NO_FREQUENCIES = new Uint8Array(0);

export function AgentVoiceVisualizer(props: AgentVoiceVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Props are read inside the animation loop through a ref, so a change of
  // speaker or phase does not tear down and restart the loop - which would
  // reset every bar to zero and make the ring visibly jump.
  const propsRef = useRef(props);
  propsRef.current = props;

  // The label is React state, but written only when the mode actually changes.
  // Setting it every frame would re-render the interview page sixty times a
  // second to change nothing.
  const modeRef = useRef<Mode>(props.active ? 'listening' : 'idle');
  const [mode, setMode] = useState<Mode>(modeRef.current);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Someone who has asked for less motion still sees the ring respond to the
    // voice - that is information, not decoration - but it stops rotating.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    let dpr = 1;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      // Capped at 2: beyond that the glow costs far more than it shows.
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const bars = new Float32Array(BAR_COUNT);
    let level = 0;
    let t = 0;
    let last = performance.now();
    let frame = 0;

    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);

      // Time-based, not frame-based: the design advanced a fixed 1/60s per
      // frame, which runs twice as fast on a 120Hz display. Capped so a tab
      // returning from the background does not jump.
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      t += dt;

      const p = propsRef.current;
      const next: Mode = !p.active ? 'idle' : p.agentSpeaking ? 'speaking' : 'listening';
      if (next !== modeRef.current) {
        modeRef.current = next;
        setMode(next);
      }

      // Outside a call there is no audio to read, and the SDK getters would
      // only be asking an idle conversation for zeros.
      const freq =
        next === 'speaking'
          ? read(p.getAgentFrequencies, NO_FREQUENCIES)
          : next === 'listening'
            ? read(p.getCandidateFrequencies, NO_FREQUENCIES)
            : NO_FREQUENCIES;
      const volume =
        next === 'speaking'
          ? read(p.getAgentLevel, 0)
          : next === 'listening'
            ? read(p.getCandidateLevel, 0)
            : 0;

      // A spectrum of all zeros is "no data", not "silence" - some transports
      // never fill it. Detected rather than assumed, so the ring falls back to
      // the design's own motion instead of lying flat through a real answer.
      let hasSpectrum = false;
      for (let i = 0; i < freq.length; i++) {
        if (freq[i]! > 0) {
          hasSpectrum = true;
          break;
        }
      }

      const W = canvas.width;
      const H = canvas.height;
      const cx = W / 2;
      const cy = H / 2;
      const S = Math.min(W, H);
      const env = envelope(next, t);
      // Per-frame easing factors from the design, converted to per-second so
      // they hold at any refresh rate.
      const followBars = 1 - Math.pow(1 - 0.22, dt * 60);
      const followLevel = 1 - Math.pow(1 - 0.1, dt * 60);

      let sum = 0;
      for (let i = 0; i < BAR_COUNT; i++) {
        const u = i / BAR_COUNT;

        // The design's idle motion: three sine bands travelling round the ring.
        const synth =
          (0.5 + 0.5 * Math.sin(u * Math.PI * 6 + t * 2.1)) * 0.45 +
          (0.5 + 0.5 * Math.sin(u * Math.PI * 14 - t * 3.4)) * 0.3 +
          (0.5 + 0.5 * Math.sin(u * Math.PI * 3 + t * 0.9)) * 0.25;

        let a: number;
        if (hasSpectrum) {
          // Mirrored left and right: the lowest frequencies sit at the bottom
          // of the ring and the highs at the top, using only the lower 45% of
          // the spectrum, where speech actually lives. Speech is bass-heavy,
          // so a real voice swells the lower half of the ring the most.
          const bin = Math.floor(Math.pow(Math.abs(u * 2 - 1), 1.3) * (freq.length * 0.45));
          a = ((freq[bin] ?? 0) / 255) * 1.45;
          // A faint breathing floor, so a pause between words reads as a
          // pause rather than as the visualiser having broken.
          a = Math.max(a, synth * 0.22);
        } else {
          a = synth;
          if (volume >= SILENCE) a *= Math.min(1.2, 0.35 + volume * 2.2);
        }

        a = Math.max(0.04, Math.min(1, a * env));
        bars[i]! += (a - bars[i]!) * followBars;
        sum += bars[i]!;
      }
      level += (sum / BAR_COUNT - level) * followLevel;

      const rotation = still ? 0 : t * 0.14;
      const r0 = S * 0.26;
      const maxLen = S * 0.105;

      ctx.clearRect(0, 0, W, H);
      ctx.lineCap = 'round';

      for (let i = 0; i < BAR_COUNT; i++) {
        const u = i / BAR_COUNT;
        const angle = u * Math.PI * 2 - Math.PI / 2 + rotation;
        const a = bars[i]!;
        const ca = Math.cos(angle);
        const sa = Math.sin(angle);
        const hue = HUE + (Math.sin(u * Math.PI * 2) * 0.5 + 0.5) * HUE_SPREAD;

        // The bar itself, glowing in proportion to its height.
        const len = S * 0.018 + a * maxLen;
        ctx.strokeStyle = `oklch(${(0.66 + a * 0.16).toFixed(3)} 0.17 ${hue.toFixed(1)} / ${(0.45 + a * 0.55).toFixed(3)})`;
        ctx.lineWidth = Math.max(2, S * 0.011);
        ctx.shadowBlur = 16 * dpr * a;
        ctx.shadowColor = `oklch(0.7 0.18 ${hue.toFixed(1)} / 0.7)`;
        ctx.beginPath();
        ctx.moveTo(cx + ca * r0, cy + sa * r0);
        ctx.lineTo(cx + ca * (r0 + len), cy + sa * (r0 + len));
        ctx.stroke();

        // Its faint echo further out.
        ctx.shadowBlur = 0;
        const gr = r0 + maxLen * 1.35 + a * S * 0.05;
        const gl = S * 0.012 + a * S * 0.045;
        ctx.strokeStyle = `oklch(0.72 0.12 ${hue.toFixed(1)} / ${(0.07 + a * 0.3).toFixed(3)})`;
        ctx.lineWidth = Math.max(1.5, S * 0.0065);
        ctx.beginPath();
        ctx.moveTo(cx + ca * gr, cy + sa * gr);
        ctx.lineTo(cx + ca * (gr + gl), cy + sa * (gr + gl));
        ctx.stroke();
      }

      // The core swells with the overall level.
      const coreR = r0 * (0.5 + level * 0.16);
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      glow.addColorStop(0, `oklch(0.72 0.16 ${HUE} / ${(0.22 + level * 0.3).toFixed(3)})`);
      glow.addColorStop(1, `oklch(0.5 0.16 ${HUE} / 0)`);
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = `oklch(0.7 0.1 ${HUE} / 0.16)`;
      ctx.lineWidth = Math.max(1, S * 0.0025);
      ctx.beginPath();
      ctx.arc(cx, cy, r0 - S * 0.03, 0, Math.PI * 2);
      ctx.stroke();
    };

    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const label =
    props.status === null
      ? null
      : (props.status ?? (mode === 'idle' ? null : LIVE_LABELS[mode]));

  return (
    <div className="agent-stage">
      {/* Announced to screen readers: during a call it is the only place the
          page says whose turn it is. Always rendered so the live region exists
          before its text changes. */}
      <div className="agent-stage-labels" aria-live="polite">
        {label ? (
          <>
            <div className="agent-stage-status">{label.title}</div>
            {label.detail ? <div className="agent-stage-detail">{label.detail}</div> : null}
          </>
        ) : null}
      </div>

      <div className="agent-stage-ring">
        <div className="agent-stage-halo" aria-hidden="true" />
        <canvas ref={canvasRef} aria-hidden="true" />
      </div>
    </div>
  );
}
